import { useEffect, useState } from "react";
import { initializeApp } from "firebase/app";
import {
  getFirestore,
  collection,
  doc,
  updateDoc,
  onSnapshot,
  increment,
  query,
  orderBy,
  arrayUnion,
} from "firebase/firestore";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

type Perfume = {
  id: string;
  name: string;
  votes: number;
  votedIPs?: string[];
};

const firebaseConfig = {
  apiKey: "AIzaSyAu-bCDoz3y6DKJkDGOp9nDMmLJgrz9f00",
  authDomain: "enquete-tp.firebaseapp.com",
  projectId: "enquete-tp",
  storageBucket: "enquete-tp.firebasestorage.app",
  messagingSenderId: "964275394951",
  appId: "1:964275394951:web:deebb4e1d80d7f19f35b22",
  measurementId: "G-CHJDWSYRRJ",
};

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

interface VotacaoPerfumesProps {
  colecao: string;
  titulo: string;
}

export default function VotacaoPerfumes({
  colecao,
  titulo,
}: VotacaoPerfumesProps) {
  const [perfumes, setPerfumes] = useState<Perfume[]>([]);
  const [votedPerfumes, setVotedPerfumes] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [userIP, setUserIP] = useState<string | null>(null);
  const [loadingIP, setLoadingIP] = useState(true);

  // Função para obter o IP do usuário
  useEffect(() => {
    const fetchIP = async () => {
      try {
        const response = await fetch("https://api.ipify.org?format=json");
        const data = await response.json();
        setUserIP(data.ip);
      } catch (error) {
        console.error("Erro ao obter IP:", error);
        // Fallback: tenta outro serviço
        try {
          const response = await fetch("https://api64.ipify.org?format=json");
          const data = await response.json();
          setUserIP(data.ip);
        } catch (fallbackError) {
          console.error("Erro ao obter IP (fallback):", fallbackError);
        }
      } finally {
        setLoadingIP(false);
      }
    };

    fetchIP();
  }, []);

  useEffect(() => {
    if (!userIP) return;

    const perfumesCollection = collection(db, colecao);
    const unsub = onSnapshot(
      query(perfumesCollection, orderBy("votes", "desc")),
      (snapshot) => {
        const data: Perfume[] = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...(doc.data() as Omit<Perfume, "id">),
        }));
        setPerfumes(data);

        // Verifica quais perfumes este IP já votou
        const voted = data
          .filter((p) => p.votedIPs?.includes(userIP))
          .map((p) => p.id);
        setVotedPerfumes(voted);
      },
    );

    return () => unsub();
  }, [colecao, userIP]);

  const handleVote = async (id: string) => {
    if (!userIP || votedPerfumes.includes(id)) return;

    const perfume = perfumes.find((p) => p.id === id);
    if (perfume?.votedIPs?.includes(userIP)) return;

    const ref = doc(db, colecao, id);
    await updateDoc(ref, {
      votes: increment(1),
      votedIPs: arrayUnion(userIP),
    });

    const newVoted = [...votedPerfumes, id];
    setVotedPerfumes(newVoted);
  };

  const filteredPerfumes = perfumes
    .filter((p) => p.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      // Se ambos têm 0 votos, ordena alfabeticamente
      if (a.votes === 0 && b.votes === 0) {
        return a.name.localeCompare(b.name, "pt-BR");
      }
      // Se apenas um tem 0 votos, coloca o com 0 votos depois
      if (a.votes === 0) return 1;
      if (b.votes === 0) return -1;
      // Se ambos têm votos, ordena por número de votos (decrescente)
      return b.votes - a.votes;
    });

  return (
    <div className="max-w-xl mx-auto p-4 text-center">
      <div className="flex items-center justify-between mb-4">
        <Link
          to="/"
          className="inline-flex items-center justify-center w-8 h-8 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-full transition-colors duration-200"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <h1 className="text-2xl font-bold flex-1">{titulo}</h1>
        <div className="w-8"></div>
      </div>
      <p className="mb-4">
        Vote nos {titulo} que você quer ver com desconto na Tabs!
      </p>

      {/* <div className="flex gap-2 mb-6">
        <input
          type="text"
          value={suggestion}
          onChange={(e) => setSuggestion(e.target.value)}
          placeholder="Sugira um perfume"
          className="flex-1 border rounded px-3 py-2"
        />
        <button
          onClick={handleSuggest}
          className="bg-black text-white px-4 py-2 rounded hover:bg-gray-800"
        >
          Sugerir
        </button>
      </div> */}

      <div className="mb-4">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar perfume..."
          className="w-full border rounded px-3 py-2"
        />
      </div>

      {loadingIP ? (
        <p className="text-gray-500">Carregando...</p>
      ) : (
        <ul className="space-y-3">
          {filteredPerfumes.map((p) => (
            <li
              key={p.id}
              className="flex justify-between items-center border rounded px-4 py-2"
            >
              <span className="text-left w-2/3">{p.name}</span>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">{p.votes} voto(s)</span>
                <button
                  onClick={() => handleVote(p.id)}
                  disabled={votedPerfumes.includes(p.id) || !userIP}
                  className={`px-3 py-1 rounded text-sm ${
                    votedPerfumes.includes(p.id) || !userIP
                      ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                      : "bg-green-500 text-white hover:bg-green-600"
                  }`}
                >
                  {votedPerfumes.includes(p.id) ? "Votado" : "Votar"}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
